import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  increment,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface Conversation {
  id: string;
  userUids: string[];
  createdAt: string;
  lastMessage?: string;
  lastMessageAt?: string;
  lastMessageSenderUid?: string;
  unreadCount?: Record<string, number>;
  /** Uids who manually flagged the thread as unread (cleared on open / mark read). */
  markedUnreadBy?: string[];
  /** Uids who hid the thread from their inbox; a new message restores it. */
  archivedBy?: string[];
}

export interface Message {
  id?: string;
  conversationId: string;
  senderUid: string;
  receiverUid: string;
  text: string;
  createdAt: string;
  participants: string[];
  /** Snapshot of the note this message replies to (notes expire; the quote stays). */
  quotedNote?: { text: string; authorUid: string };
}

export const conversationService = {
  subscribeToConversations(
    uid: string,
    callback: (conversations: Conversation[]) => void,
    onError?: (error: Error) => void,
    enabled = true
  ) {
    if (!enabled) {
      callback([]);
      return () => {};
    }

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('userUids', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc')
    );
    return onSnapshot(
      conversationsQuery,
      snap => {
        const conversations: Conversation[] = [];
        snap.forEach(docSnap => {
          conversations.push({ id: docSnap.id, ...docSnap.data() } as Conversation);
        });
        callback(conversations);
      },
      error => {
        console.error('Conversations subscription failed:', error);
        if (onError) {
          onError(error);
        } else {
          callback([]);
        }
      }
    );
  },

  async getConversation(conversationId: string): Promise<Conversation | null> {
    const convRef = doc(db, 'conversations', conversationId);
    const convSnap = await getDoc(convRef);
    if (convSnap.exists()) {
      return { id: convSnap.id, ...convSnap.data() } as Conversation;
    }
    return null;
  },

  async createConversation(userUids: string[]): Promise<string> {
    const sortedUids = [...userUids].sort();
    const conversationId = sortedUids.join('_');
    const convRef = doc(db, 'conversations', conversationId);

    // get on a missing doc fails rules (resource is null) — treat that as "not found"
    let exists: boolean;
    try {
      const convSnap = await getDoc(convRef);
      exists = convSnap.exists();
    } catch {
      exists = false;
    }

    if (!exists) {
      const now = new Date().toISOString();
      await setDoc(convRef, {
        id: conversationId,
        userUids: sortedUids,
        createdAt: now,
        lastMessage: '',
        lastMessageAt: now,
        lastMessageSenderUid: '',
        unreadCount: Object.fromEntries(sortedUids.map(id => [id, 0])),
        markedUnreadBy: [],
        archivedBy: [],
      });
    }

    return conversationId;
  },

  /** Hide a conversation for one participant until the next message. */
  async archiveConversation(uid: string, conversationId: string): Promise<void> {
    const convRef = doc(db, 'conversations', conversationId);
    await updateDoc(convRef, {
      archivedBy: arrayUnion(uid),
    });
  },

  subscribeToMessages(
    conversationId: string,
    uid: string,
    callback: (messages: Message[]) => void,
    onError?: (error: Error) => void
  ) {
    // participants filter required so list rules can authorize the query
    const messagesQuery = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      where('participants', 'array-contains', uid),
      orderBy('createdAt', 'asc')
    );

    return onSnapshot(
      messagesQuery,
      snapshot => {
        const messages: Message[] = [];
        snapshot.forEach(docSnap => {
          messages.push({ id: docSnap.id, ...docSnap.data() } as Message);
        });
        callback(messages);
      },
      error => {
        console.error('Messages subscription failed:', error);
        if (onError) {
          onError(error);
        } else {
          callback([]);
        }
      }
    );
  },

  async sendDirectMessage(
    senderUid: string,
    receiverUid: string,
    text: string,
    quotedNote?: Message['quotedNote']
  ): Promise<{ conversationId: string; messageId: string }> {
    const conversationId = await conversationService.createConversation([senderUid, receiverUid]);
    const messageId = await conversationService.sendMessage({
      conversationId,
      senderUid,
      receiverUid,
      text,
      createdAt: new Date().toISOString(),
      participants: [senderUid, receiverUid].sort(),
      ...(quotedNote ? { quotedNote } : {}),
    });
    return { conversationId, messageId };
  },

  async sendMessage(message: Omit<Message, 'id'>): Promise<string> {
    const batch = writeBatch(db);
    const msgRef = doc(collection(db, 'messages'));
    batch.set(msgRef, message);

    const convRef = doc(db, 'conversations', message.conversationId);
    batch.update(convRef, {
      lastMessage: message.text,
      lastMessageAt: message.createdAt,
      lastMessageSenderUid: message.senderUid,
      [`unreadCount.${message.receiverUid}`]: increment(1),
      archivedBy: [],
    });

    await batch.commit();
    return msgRef.id;
  },

  async markConversationRead(uid: string, conversationId: string): Promise<void> {
    const convRef = doc(db, 'conversations', conversationId);
    await updateDoc(convRef, {
      [`unreadCount.${uid}`]: 0,
      markedUnreadBy: arrayRemove(uid),
    });
  },

  async markConversationUnread(uid: string, conversationId: string): Promise<void> {
    const convRef = doc(db, 'conversations', conversationId);
    await updateDoc(convRef, {
      markedUnreadBy: arrayUnion(uid),
    });
  },
};

export function conversationUnreadCount(conversation: Conversation, uid: string): number {
  return conversation.unreadCount?.[uid] ?? 0;
}

/** Unread badge state: real unread messages or a manual "mark as unread" flag. */
export function isConversationUnread(conversation: Conversation, uid: string): boolean {
  return (
    conversationUnreadCount(conversation, uid) > 0 ||
    Boolean(conversation.markedUnreadBy?.includes(uid))
  );
}
